import jwt_decode from 'jwt-decode'
import Keycloak from 'keycloak-js'
import { sendTrackEvent } from './Events'
import RedirectManager from './RedirectManager'
import { refreshToken } from './RefreshToken'
import { GetStorage, GetTokenStorageKey } from './StorageKeys'
import { checkBrowserIsWebview } from './Utils'

class SessionManager {

  constructor(settings) {
    this.settings = settings


    this.isInitialized = false
    this.keyCloakInstance = null
    this.oidcIssuer = this.settings && ['id.globo', 'id.qa.globoi'].some(domain => this.settings.url.includes(domain)) ? 'RHSSO' : 'GOIDC'
  }

  async setup() {
    const initRes = await this.initialize()
    this.cleanUpUrl()
    return initRes
  }
  
  async initialize() {
    if (this.isInitialized) {
      console.debug('SessionManager already initialized. Doing nothing')
    } else {
      this.keyCloakInstance = Keycloak(this.settings)


      const silentLoginEvent = {
        clientId: this.settings.clientId,
        glbId: this.getCookieGLBID(),
        sdkVersion: this.settings.sdkVersion,
        eventType: `SDK.WEB.${this.oidcIssuer}.SSO.SILENT`,
        isWebView: checkBrowserIsWebview(),
        sessionManagement: this.settings.sessionManagement,
        legacyCompatible: this.settings.legacyCompatible
      }
      
      try {
        console.debug(
          `Trying to init keycloak instance with these settings:
        ${JSON.stringify(this.settings)}`
        )

        // fix white screen on silent login
        if (this.settings) this.settings = {
          ...this.settings,
          messageReceiveTimeout: 5000,
          silentCheckSsoFallback: false
        }

        let hasExecutedRefreshToken = false
        try {
          const refreshTokens = await this.RefreshTokens()
          if (refreshTokens && this.settings.enableRefreshTokenMode) hasExecutedRefreshToken = true
        } catch (e) {
          if (this.settings.onLoad === 'check-sso') this.settings.silentCheckSsoRedirectUri = this.settings.silentCheckSsoRedirectUri || this.settings.redirectUri
        }

        return {
          hasInitialized: await this.iniCheckingSSO(silentLoginEvent),
          hasExecutedRefreshToken,
        }
      } catch (e) {
        silentLoginEvent.eventType = `${silentLoginEvent.eventType}.FAILED`
        if (this.settings.onLoad === 'check-sso') {
          silentLoginEvent.eventType = `${silentLoginEvent.eventType}.CONNECTIVITY_ERROR`
        }
        sendTrackEvent(silentLoginEvent)
        const errorMsg = `Error initializing keycloak instance. settings:
      ${JSON.stringify(this.settings)}`
        console.error(errorMsg)
        throw e
      }
    }

  }

  async RefreshTokens(fallback = false) {
    const refreshTokenEvent = {
      clientId: this.settings.clientId,
      glbId: this.getCookieGLBID(),
      globoId: this.getUserGloboId(),
      sdkVersion: this.settings.sdkVersion,
      eventType: `SDK.WEB.${this.oidcIssuer}.REFRESH_TOKEN`,
      isWebView: checkBrowserIsWebview(),
      sessionManagement: this.settings.sessionManagement,
      legacyCompatible: this.settings.legacyCompatible,
    }

    if (this.settings.sessionManagement === 'token' && this.getTokensFromLocalStorage()) {
      refreshTokenEvent.fsId = this.getTokenData() && this.getTokenData().fs_id
    }
    
    if (this.settings.enableRefreshTokenMode) {
      try {
        const tokens = JSON.parse(this.getTokensFromLocalStorage())
        if (tokens){
          const refreshTokens = await refreshToken(tokens, this.settings.clientId)

          this.saveRefreshedTokens(refreshTokens)

          refreshTokenEvent.eventType = `${refreshTokenEvent.eventType}.SUCCEEDED`

          sendTrackEvent(refreshTokenEvent)
          
          return refreshTokens
        }
      } catch (error) {
        refreshTokenEvent.eventType = `${refreshTokenEvent.eventType}.FAILED`
        refreshTokenEvent.errorReason = error.message


        sendTrackEvent(refreshTokenEvent)
        console.debug('Error refreshing token: ', error)
        
        if(error.invalidTokenError) {
          this.cleanUpTokens() 
          return
        }
        
        if (fallback) return JSON.parse(this.getTokensFromLocalStorage())

        throw error
      }
    } else {
      return JSON.parse(this.getTokensFromLocalStorage())
    } 
  }

  cleanUpTokens() {
    this.removeEntriesStartingWith(this.getStorageKeyPrefix(), localStorage)
    this.removeEntriesStartingWith(this.getStorageKeyPrefix(), sessionStorage)
  }

  removeEntriesStartingWith(prefix, storage) {
    Object.keys(storage).forEach(key => {
      if (key.startsWith(prefix)) {
        storage.removeItem(key)
      }
    })
  }

  jwtDecodeAccesToken(accessToken) {
    return jwt_decode(accessToken)
  }


  async iniCheckingSSO(silentLoginEvent) {
    const initResult = this.settings && this.settings.onLoad === 'check-sso' ? await this.silentLoginWithFallback() : await this.keyCloakInstance.init(this.settings)
    
    if (this.settings.onLoad === 'check-sso') {
      const tokens = this.getTokensFromLocalStorage()
      const tokenData = tokens ? this.getTokenData() : null
      const expectedCookies = ['GLOBO_ID', 'GLBID']

      silentLoginEvent.fsId = tokenData && tokenData.fs_id ? tokenData.fs_id : null
      silentLoginEvent.globoId = tokenData && tokenData.globo_id ? tokenData.globo_id : null

      if (initResult) {
        silentLoginEvent.eventType = `${silentLoginEvent.eventType}.SUCCEEDED`
        sendTrackEvent(silentLoginEvent)
      } else {
        if (this.hasNecessaryCookies(expectedCookies)) {
          silentLoginEvent.eventType = `${silentLoginEvent.eventType}.FAILED.SERVER_ERROR`
          sendTrackEvent(silentLoginEvent)
        } else if (!this.hasNecessaryCookies(expectedCookies) && tokens) {
          silentLoginEvent.eventType = `${silentLoginEvent.eventType}.FAILED.MISSING_COOKIES`
          sendTrackEvent(silentLoginEvent)
        }
      }
    }

    this.isInitialized = true
    return initResult
  }

  getTokensFromLocalStorage() {
    return GetStorage('local').getItem(GetTokenStorageKey(this.settings.clientId))
  }

  saveRefreshedTokens(tokens) {
    const storeKey = GetTokenStorageKey(this.settings.clientId)
    const {access_token, id_token, refresh_token} = tokens
    localStorage.setItem(storeKey, JSON.stringify({access_token, id_token, refresh_token}))
    sessionStorage.setItem(storeKey, JSON.stringify({access_token, id_token, refresh_token}))
  }

  async silentLoginWithFallback(limitTries = 1) {
    return await this.keyCloakInstance.init(this.settings).then(isAuthenticated => isAuthenticated).catch(async (e) => {
      if (!limitTries) {

        console.error('Error initializing keycloak instance with silent login - number of tries exceeded', e)
        throw e
      }
      console.error('Error initializing keycloak instance with silent login - executing fallback', e)
      return await this.silentLoginWithFallback(limitTries - 1)
    })
  }

  async login() {
    await this.initialize()

    const options = {
      redirectUri: this.settings.redirectUri,
      ...(!this.hasNecessaryCookiesForLegacyCompatibility() && { prompt: 'login' }),
    }

    await this.keyCloakInstance.login(options)
  }

  async loginCustomSettings(settings) {
    if (!settings) {
      throw new Error('The required custom settings was not passed correctly')
    }

    await this.initialize()

    const { loginURL, stateId } = this.createLoginUrlCustomSettings(settings)
    await this.storeStateIfNeeded(settings, stateId)
    window.location = loginURL
  }

  async storeStateIfNeeded(options, stateId) {
    if (options.storeState) {
      console.debug('Building application state')

      return new RedirectManager().storeState({
        ...this.settings,
        ...options,
        customNonce: stateId,
        tokenServerUrl: this.keyCloakInstance.endpoints.token()
      })
    }
  }

  createLoginUrlCustomSettings(options) {
    const loginUrl = this.keyCloakInstance.createLoginUrl(
      this.settings.redirectUri
    )
    let searchParams = new URLSearchParams(loginUrl)

    const funnelMetrics = options.funnelMetrics
    if (funnelMetrics) {
      Object.keys(funnelMetrics).forEach((key) => {
        searchParams.append(key, encodeURIComponent(funnelMetrics[key]))
      })
    }

    if (!this.hasNecessaryCookiesForLegacyCompatibility()) {
      searchParams.append('prompt', 'login')
    }

    return { loginURL: decodeURIComponent(searchParams.toString()), stateId: searchParams.get('state') }
  }

  async logout() {
    const cookieGLBID = ['GLBID']
    const clientId = this.settings.clientId
    
    const tokens = this.settings.sessionManagement === 'token' && JSON.parse(this.getTokensFromLocalStorage())
    const tokenData = tokens && this.getTokenData()

    const logoutEvent = {
      clientId,
      sdkVersion: this.settings.sdkVersion,
      eventType: this.hasNecessaryCookies(cookieGLBID) ? 'SDK.WEB.GLBID.LOGOUT.REQUESTED' : 'SDK.WEB.OIDC.LOGOUT.REQUESTED',
      sessionManagement: this.settings.sessionManagement,
      legacyCompatible: this.settings.legacyCompatible
    }

    const hasNecessaryCookies = this.hasNecessaryCookies(cookieGLBID)
    logoutEvent.glbId = hasNecessaryCookies ? this.getCookieGLBID() : undefined

    logoutEvent.fsId = tokenData && tokenData.fs_id ? tokenData.fs_id : null
    logoutEvent.globoId = this.getUserGloboId() 
    
    sendTrackEvent(logoutEvent)

    const WithIdToken = `${tokens && tokens['id_token'] ? `&id_token=${tokens['id_token'] }` : ''}`

    if (this.settings.logoutUrl) {
      return window.location = `${this.settings.logoutUrl}/logout?url=${encodeURIComponent(window.location.href)}&client_id=${clientId}${WithIdToken}`
    }

    // eslint-disable-next-line no-undef
    return window.location = `${AUTHX_FE_URL}/logout?url=${encodeURIComponent(window.location.href)}&client_id=${clientId}${WithIdToken}`
 
  }

  async logoutGoidc() {
    // eslint-disable-next-line no-undef
    const response = await fetch(`${GOIDC_HOST}/auth/realms/${this.settings.realm}/protocol/openid-connect/session/logout`, {
      method: 'POST',
      credentials: 'include',
    })

    if (response.status !== 204) {
      throw new Error('[GloboID.js] Logout error')
    }
  }

  async logoutRhsso() {
    // eslint-disable-next-line no-undef
    window.location = `${RHSSO_HOST}/auth/realms/${this.settings.realm}/protocol/openid-connect/logout?redirect_uri=${encodeURIComponent(window.location.href)}`
  }

  loadMemoryInfo() {
    const kcInstance = this.keyCloakInstance
    return {
      id_token: kcInstance.idToken,
      refresh_token: kcInstance.refreshToken,
      access_token: kcInstance.token
    }
  }

  getCodeVerifier() {
    this.keyCloakInstance.getCodeVerifier()
  }

  getCookies() { return document.cookie }

  hasNecessaryCookies(cookieList) {
    const rawCookies = this.getCookies()
    const cookies = rawCookies
      ? rawCookies.split(' ').filter(cookie => cookieList.includes(cookie.split('=')[0]))
      : []

    return cookies.length === cookieList.length
  }

  cleanUpUrl() {
    console.debug('Cleaning up url')

    let splitUrl = window.location.href.split('#')

    if (splitUrl.length === 0) {
      return
    }

    let tokenList = splitUrl[1]

    if (!tokenList) {
      return
    }

    tokenList = tokenList
      .split('&')
      .filter(
        (el) => !el.startsWith('session_state')
          && !el.startsWith('code')
      )

    splitUrl[1] = tokenList.join('&')

    history.pushState({ page: 1 }, window.title, splitUrl.join('#'))
  }

  hasNecessaryCookiesForLegacyCompatibility() {
    if (!this.settings.legacyCompatible) {
      return true
    }
    return this.hasNecessaryCookies(['GLBID'])
  }

  getUserGloboId() {
    let globoId = ''

    try {
      if (this.settings.sessionManagement === 'cookie') {
        const cookies = document.cookie.split('; ')
        for (const cookie of cookies) {
          const [key, value] = cookie.split('=')
          if (key === 'GLOBO_ID') {
            globoId = jwt_decode(decodeURIComponent(value))['globoId']
          }
        }
      } else {
        let idToken = this.settings.id_token
  
        if (!idToken) {
          const tokens = this.getTokensFromLocalStorage() 
  
          idToken = tokens && JSON.parse(tokens).id_token
        }
  
        const decoded_jwt = idToken && jwt_decode(idToken)
        globoId = decoded_jwt ? decoded_jwt['globo_id'] : ''
      }
    } catch (e) {
      console.error('Error getting GloboId', e.message)
    }

    return globoId
  }

  getCookieGLBID() {
    const cookieGLBID = ['GLBID']
    return this.hasNecessaryCookies(cookieGLBID) ? this.getCookies().split(' ').filter(cookie => cookieGLBID.includes(cookie.split('=')[0]))[0].split('=')[1] : ''
  }

}

export default SessionManager
